import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { ReportSheet } from "@/components/ReportSheet";

const today = () => new Date().toLocaleDateString("en-CA");

export const ReportDialog = ({ open, onOpenChange, groups }) => {
  const [date, setDate] = useState(today());
  const [gid, setGid] = useState("all");
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (open && date) api.get("/entries", { params: { date_from: date, date_to: date } }).then((r) => setEntries(r.data));
  }, [open, date]);

  const blocks = groups.filter((g) => gid === "all" || g.id === gid)
    .map((g) => ({ group: g, rows: entries.filter((e) => e.group_id === g.id).sort((a, b) => a.created_at.localeCompare(b.created_at)) }))
    .filter((b) => b.rows.length);

  const render = async () => {
    const url = await toPng(ref.current, { pixelRatio: 2, backgroundColor: "#fff" });
    return { url, name: `report-${date}.png` };
  };

  const download = async () => {
    setBusy(true);
    try { const { url, name } = await render(); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); }
    catch { toast.error("Could not create image"); }
    setBusy(false);
  };

  const share = async () => {
    setBusy(true);
    try {
      const { url, name } = await render();
      const file = new File([await (await fetch(url)).blob()], name, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: name });
      else { await navigator.clipboard.write([new ClipboardItem({ "image/png": file })]); toast.success("Image copied — paste it in WhatsApp/chat"); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing not supported here, use Download"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-dialog" className="sm:max-w-3xl" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-head text-2xl">Report image</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-xs text-slate-500 space-y-1"><span>Date</span>
            <Input data-testid="report-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="font-mono w-44" />
          </label>
          <label className="text-xs text-slate-500 space-y-1 min-w-[180px]"><span>Group</span>
            <Select value={gid} onValueChange={setGid}>
              <SelectTrigger data-testid="report-group-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
        </div>
        <div className="border rounded-lg bg-slate-100 p-3 max-h-[55vh] overflow-auto">
          {blocks.length ? <ReportSheet blocks={blocks} date={date} innerRef={ref} />
            : <p data-testid="report-empty" className="text-sm text-slate-500 p-6 text-center">No entries for this date</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" data-testid="report-share-btn" disabled={!blocks.length || busy} onClick={share}><Share2 className="w-4 h-4 mr-2" />Share</Button>
          <Button data-testid="report-download-btn" disabled={!blocks.length || busy} onClick={download} className="bg-sky-600 hover:bg-sky-700">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Download PNG
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
